import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type MobileTab = string;

export interface MobileTabConfig {
  key: string;
  label: string;
  icon: string;
  badge?: number;
}

export const MOBILE_TABS: MobileTabConfig[] = [
  { key: 'materials', label: 'Materials', icon: 'bi-box-seam' },
  { key: 'directLabour', label: 'Direct', icon: 'bi-person-gear' },
  { key: 'indirectLabour', label: 'Indirect', icon: 'bi-people' },
  { key: 'fixedOverheads', label: 'Fixed OH', icon: 'bi-building' },
  { key: 'variableOverheads', label: 'Variable OH', icon: 'bi-graph-up' },
  { key: 'competitors', label: 'Competitors', icon: 'bi-trophy' },
];

@Injectable({ providedIn: 'root' })
export class MobileNavService {
  private tabs$ = new BehaviorSubject<MobileTabConfig[]>(MOBILE_TABS);
  tabs = this.tabs$.asObservable();

  private activeTab$ = new BehaviorSubject<string>('materials');
  activeTab = this.activeTab$.asObservable();

  private navVisible$ = new BehaviorSubject<boolean>(true);
  navVisible = this.navVisible$.asObservable();

  private badgeCounts$ = new BehaviorSubject<Record<string, number>>({
    materials: 0, directLabour: 0, indirectLabour: 0,
    fixedOverheads: 0, variableOverheads: 0, competitors: 0,
  });
  badgeCounts = this.badgeCounts$.asObservable();

  private firstLogin$ = new BehaviorSubject<boolean>(false);
  firstLogin = this.firstLogin$.asObservable();

  setFirstLogin(val: boolean): void { this.firstLogin$.next(val); }
  get isFirstLogin(): boolean { return this.firstLogin$.value; }

  showNav(): void { this.navVisible$.next(true); }
  hideNav(): void { this.navVisible$.next(false); }

  setTabs(tabs: MobileTabConfig[], defaultActiveTab?: string): void {
    this.tabs$.next(tabs);
    if (defaultActiveTab) {
      this.activeTab$.next(defaultActiveTab);
      return;
    }

    const firstKey = tabs[0]?.key;
    if (firstKey && !tabs.some(t => t.key === this.activeTab$.value)) {
      this.activeTab$.next(firstKey);
    }
  }

  resetTabs(): void {
    this.setTabs(MOBILE_TABS, 'materials');
  }

  updateBadges(counts: Partial<Record<string, number>>): void {
    const merged: Record<string, number> = { ...this.badgeCounts$.value };
    for (const key of Object.keys(counts)) {
      const val = counts[key];
      if (val !== undefined) merged[key] = val;
    }
    this.badgeCounts$.next(merged);
  }

  get currentTab(): string {
    return this.activeTab$.value;
  }

  setActiveTab(tab: string): void {
    this.activeTab$.next(tab);
  }

  nextTab(): void {
    const tabs = this.tabs$.value;
    const idx = tabs.findIndex(t => t.key === this.currentTab);
    if (idx < tabs.length - 1) {
      this.activeTab$.next(tabs[idx + 1].key);
    }
  }

  prevTab(): void {
    const tabs = this.tabs$.value;
    const idx = tabs.findIndex(t => t.key === this.currentTab);
    if (idx > 0) {
      this.activeTab$.next(tabs[idx - 1].key);
    }
  }
}
